import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import UIButton from 'Components/UIComponents/UIButton';
import dayjs from 'dayjs';
import { useShowTrialSuccessPageClear } from 'Hooks/Auth';
import { discountPlanPrices, PlanDurations, PlanTypes } from 'Interfaces/Billing';
import { AuthorizedRoutePaths } from 'Interfaces/RoutePaths';
import { User } from 'Interfaces/User';
import SuccessPageWrapper from 'Layouts/SuccessPageWrapper';
import { DataLayerAnalytics } from 'Services/Integrations';
import { formatDateToStringForTrialInfo } from 'Utils/formatters';
import { selectUser } from 'Utils/selectors';

interface TrialSuccessProps {
  successRequired?: boolean;
}

function TrialSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as TrialSuccessProps;

  const [clearShowTrialSuccessPage] = useShowTrialSuccessPageClear();
  const { id }: User = useSelector(selectUser);

  if (!locationState.successRequired) navigate(AuthorizedRoutePaths.BASE_PATH);

  const price = discountPlanPrices[PlanTypes.BUSINESS][PlanDurations.MONTHLY];
  const nextBillingDate = dayjs(new Date())
    .add(7, 'day')
    .format('MMMM D, YYYY');

  const handleLearnMore = () => {
    navigate(AuthorizedRoutePaths.SETTINGS_BILLING, { replace: true });
  };

  const handleContinue = () => {
    navigate(AuthorizedRoutePaths.BASE_PATH, { replace: true });
  };

  useEffect(() => {
    DataLayerAnalytics.fireTrialSignUpEvent(id);
    clearShowTrialSuccessPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SuccessPageWrapper>
      <div className="successPages__inner">
        <div className="successPages__wrapper">
          <p className="successPages__header">
            Thank you for signing up for our 7-day free trial!
          </p>
          <p className="successPages__text">
            We&apos;re excited to have you onboard. As a reminder, your trial will
            automatically transition to a full subscription for <b>${price}/month</b> on{' '}
            <b>{formatDateToStringForTrialInfo(nextBillingDate)}</b>. If you&apos;d like
            to continue enjoying our service without interruption, no action is needed. If
            you have any questions or would like to learn more about your subscription,
            we&apos;re here to help.
          </p>
          <UIButton
            priority="primary"
            title={'Learn More'}
            handleClick={handleLearnMore}
            className="successPages__button--download"
          />
          <div className="successPages__button--cancel" onClick={handleContinue}>
            Continue
          </div>
        </div>
      </div>
    </SuccessPageWrapper>
  );
}

export default TrialSuccess;
